// Utility to compute today's schedule and open-now status based on openingHours schema

function getNowInTimezone(timezone) {
    const fmt = new Intl.DateTimeFormat('en-CA', {
        timeZone: timezone || 'Europe/Podgorica',
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: false,
    });
    const parts = Object.fromEntries(fmt.formatToParts(new Date()).map(p => [p.type, p.value]));
    const year = parseInt(parts.year, 10);
    const month = parseInt(parts.month, 10);
    const day = parseInt(parts.day, 10);
    const hour = parseInt(parts.hour, 10);
    const minute = parseInt(parts.minute, 10);
    // weekday in tz
    const weekdayFmt = new Intl.DateTimeFormat('en-US', { timeZone: timezone || 'Europe/Podgorica', weekday: 'short' });
    const weekday = weekdayFmt.format(new Date()).toLowerCase().slice(0, 3); // mon,tue,...
    return { year, month, day, hour, minute, weekday };
}

function toDateKey(year, month, day) {
    const mm = String(month).padStart(2, '0');
    const dd = String(day).padStart(2, '0');
    return `${year}-${mm}-${dd}`;
}

function parseTimeToMinutes(timeStr) {
    if (!timeStr || typeof timeStr !== 'string') return null;
    const [hh, mm] = timeStr.split(':').map((v) => parseInt(v, 10));
    if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
    return hh * 60 + mm;
}

function dateKeyInRange(dateKey, fromKey, toKey) {
    if (!fromKey || !toKey) return false;

    // Всегда сравниваем только месяц-день, игнорируя год
    const todayMMDD = dateKey.slice(5); // YYYY-MM-DD -> MM-DD
    const fromMMDD = fromKey.length === 5 ? fromKey : fromKey.slice(5); // MM-DD или YYYY-MM-DD -> MM-DD
    const toMMDD = toKey.length === 5 ? toKey : toKey.slice(5); // MM-DD или YYYY-MM-DD -> MM-DD

    return todayMMDD >= fromMMDD && todayMMDD <= toMMDD;
}

function matchesExceptionToday(exc, todayKey) {
    // Support single date or range
    if (exc?.date) {
        // Всегда сравниваем только месяц-день, игнорируя год
        const todayMMDD = todayKey.slice(5); // YYYY-MM-DD -> MM-DD
        const excMMDD = String(exc.date).length === 5 ? String(exc.date) : String(exc.date).slice(5);
        return todayMMDD === excMMDD;
    }
    if (exc?.from && exc?.to) {
        // Всегда сравниваем только месяц-день, игнорируя год
        return dateKeyInRange(todayKey, exc.from, exc.to);
    }
    return false;
}

function getSeasonHoursForToday(seasons, todayKey, weekday) {
    if (!Array.isArray(seasons)) return [];
    // check in order; take first matching season
    for (const s of seasons) {
        if (!s?.from || !s?.to || !Array.isArray(s?.days) || !Array.isArray(s?.hours)) continue;
        if (!dateKeyInRange(todayKey, s.from, s.to)) continue;
        if (!s.days.includes(weekday)) continue;
        // return array of { start, end }
        return s.hours
            .map(h => ({ start: h?.start, end: h?.end }))
            .filter(h => h.start && h.end);
    }
    return [];
}

export function computeTodayOpeningStatus(openingHours) {
    if (!openingHours || typeof openingHours !== 'object') return { isOpenNow: null, todayIntervals: [] };
    const tz = openingHours.timezone || 'Europe/Podgorica';
    const now = getNowInTimezone(tz);
    const todayKey = toDateKey(now.year, now.month, now.day);
    const weekdayMap = { mon: 'mon', tue: 'tue', wed: 'wed', thu: 'thu', fri: 'fri', sat: 'sat', sun: 'sun' };
    const weekday = weekdayMap[now.weekday] || now.weekday;

    // exceptions first
    const exceptions = Array.isArray(openingHours.exceptions) ? openingHours.exceptions : [];
    const matchedException = exceptions.find((e) => matchesExceptionToday(e, todayKey));
    if (matchedException) {
        if (matchedException.type === 'closed') {
            return { isOpenNow: false, todayIntervals: [], timezone: tz };
        }
        if (matchedException.type === 'openCustom' && Array.isArray(matchedException.hours)) {
            const intervals = matchedException.hours
                .map(h => ({ start: h?.start, end: h?.end }))
                .filter(h => h.start && h.end);
            const nowMin = now.hour * 60 + now.minute;
            const open = intervals.some(iv => {
                const s = parseTimeToMinutes(iv.start);
                const e = parseTimeToMinutes(iv.end);
                return s !== null && e !== null && nowMin >= s && nowMin < e;
            });
            return { isOpenNow: open, todayIntervals: intervals, timezone: tz };
        }
    }

    // seasons fallback
    const seasons = Array.isArray(openingHours.seasons) ? openingHours.seasons : [];
    const intervals = getSeasonHoursForToday(seasons, todayKey, weekday);
    if (!intervals.length) return { isOpenNow: false, todayIntervals: [], timezone: tz };
    const nowMin = now.hour * 60 + now.minute;
    const open = intervals.some(iv => {
        const s = parseTimeToMinutes(iv.start);
        const e = parseTimeToMinutes(iv.end);
        return s !== null && e !== null && nowMin >= s && nowMin < e;
    });
    return { isOpenNow: open, todayIntervals: intervals, timezone: tz };
}

export function formatIntervals(intervals) {
    if (!Array.isArray(intervals) || intervals.length === 0) return '';
    return intervals.map(iv => `${iv.start}-${iv.end}`).join(', ');
}


